import React, { useCallback, useEffect, useMemo, useState } from "react";

import { last } from "lodash";
import { RouteComponentProps } from "react-router-dom";
import {
  JsonParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import useNormalizedEvaluationOptions from "../../hooks/useNormalizedEvaluationOptions";
import useTranslations from "../../hooks/useTranslations";

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  H6,
  Link,
  ScreenContainer,
  ScreenHeader,
  Select,
} from "../wombat-ui";
import { AnalyticsIcon, ChevronDownIcon } from "../icons";

import Pagination from "../utils/Pagination";

import useViewer from "../../hooks/useViewer";

import SearchPopover from "../utils/SearchPopover";
import SearchPopoverHeading from "../utils/SearchPopoverHeading";
import SearchPopoverItem from "../utils/SearchPopoverItem";

import EvaluationEvolutionDetail from "./EvaluationEvolutionDetail";
import List from "../utils/List";
import UserAvatar from "../utils/avatars/UserAvatar";
import UserGradeHorizontalBarChart from "./charts/UserGradeHorizontalBarChart";
import useLastUserGradeChartData from "./hooks/useLastUserGradeChartData";
import { Tag, TagCloseButton } from "@chakra-ui/react";
import useFuzzySearch from "../../hooks/useFuzzySearch";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import {
  GroupFragment,
  useGetAnswersLazyQuery,
  useGetUsersMinimal,
  useGetUsersMinimalLazyQuery,
  UserMinimalFragment,
} from "../../graphql/generated/definitions";

type EvaluationAnalyticsDetailProps = RouteComponentProps<{ digest: string }>;

const EvaluationAnalyticsDetail: React.FC<EvaluationAnalyticsDetailProps> = ({
  match,
}) => {
  const t = useTranslations();
  const {
    company: { groups },
  } = useViewer();

  const options = useNormalizedEvaluationOptions();
  const optionsInterval = useMemo(
    () => options?.[1]?.value / 2 || 0.01,
    [options]
  );

  const { digest } = match.params;
  const [{ usersPage, usersByLevelPage, groupIds, selectedLevel }, setQuery] =
    useQueryParams({
      userId: StringParam,
      usersPage: withDefault(NumberParam, 1),
      usersByLevelPage: withDefault(NumberParam, 1),
      groupIds: withDefault(JsonParam, []),
      selectedLevel: withDefault(NumberParam, last(options).value),
    });

  const [groupSearch, setGroupSearch] = useState("");

  const { data: usersData, loading: usersLoading } = useGetUsersMinimal({
    variables: {
      page: usersPage,
      per: 20,
      havingAnswersMatching: [
        {
          digest,
          minimumNormalizedRating: 0,
          maximumNormalizedRating: 1,
          lastCreatedAt: "now",
        },
      ],
      groupIds: groupIds.map(({ id }) => id),
    },
  });

  const [getUsers, { data: usersByLevelData, loading: usersByLevelLoading }] =
    useGetUsersMinimalLazyQuery();
  const usersByLevel = usersByLevelData?.users.collection || [];

  const [getAnswers, { data: answersData, loading: answersLoading }] =
    useGetAnswersLazyQuery();
  const answers = answersData?.answers.collection || [];

  useEffect(() => {
    if (usersData)
      getAnswers({
        variables: {
          questionDigest: [digest],
          lastCreatedAt: "now",
          collaboratorIds: usersData.users.collection.map(({ id }) => id),
        },
      });
  }, [usersData]);

  const evaluation = useMemo(() => (
    answers?.[0]?.question?.label
  ), [answers]);

  useBreadcrumbs([
    {
      to: "/analytics/evaluation-mapping",
      text: t("statistics.evaluations") as string,
    },
    {
      to: `/analytics/evaluations/${match.params.digest}`,
      text: evaluation,
    },
  ]);

  const chartData = useLastUserGradeChartData(answers, options);

  const handleGroupSelected = useCallback(
    ({ id, name }) => {
      setQuery({
        groupIds: [...groupIds, { id, name }],
      });
    },
    [groupIds]
  );

  useEffect(
    () =>
      getUsers({
        variables: {
          page: usersByLevelPage,
          per: 6,
          groupIds: groupIds.map(({ id }) => id),
          havingAnswersMatching: [
            {
              digest,
              minimumNormalizedRating: selectedLevel - optionsInterval,
              maximumNormalizedRating: selectedLevel + optionsInterval,
              lastCreatedAt: "now",
            },
          ],
        },
      }),
    [selectedLevel, usersByLevelPage, groupIds, digest]
  );

  const filteredGroups = useMemo(() => {
    return (
      groups.filter(
        ({ id }) => !groupIds.find(({ id: filteredId }) => filteredId === id)
      ) || []
    );
  }, [groups, groupIds]);

  const searchFilteredGroup = useFuzzySearch(filteredGroups, groupSearch, {
    keys: ["name"],
    threshold: 0.3,
    useExtendedSearch: true,
    matchIfEmpty: true,
  }).map(({ item }) => item);

  return (
    <ScreenContainer>
      <ScreenHeader
        title={evaluation}
        description={t("statistics.evaluation")}
        icon={AnalyticsIcon}
        actions={[
          <Link
            key={"back"}
            type="button"
            variant="button-subtle"
            to={"/analytics/evaluation-mapping"}
          >
            {t("header.goBack")}
          </Link>,
        ]}
      />

      <Container className="flex flex-col w-full">
        <div className="flex mt-6">
          <SearchPopover
            title={
              <SearchPopoverHeading>
                {t("collaborator.groups")}
              </SearchPopoverHeading>
            }
            loading={false}
            items={searchFilteredGroup}
            onItemSelected={handleGroupSelected}
            onSearchChange={setGroupSearch}
            searchValue={groupSearch}
            button={
              <Button variant="subtle" trailingIcon={ChevronDownIcon}>
                {t("statistics.filters.byGroups")}
              </Button>
            }
            noMatch={
              <SearchPopoverItem>
                {t("wombat.select.emptyResult")}
              </SearchPopoverItem>
            }
            renderItem={(item: GroupFragment) => (
              <SearchPopoverItem>{item.name}</SearchPopoverItem>
            )}
          />
        </div>
        <div className="flex gap-2 py-2">
          {groupIds.map(({ id, name }) => (
            <Tag key={id} colorScheme="yellow">
              {name}
              <TagCloseButton
                onClick={() =>
                  setQuery({
                    usersPage: 1,
                    groupIds: groupIds.filter(
                      ({ id: groupId }) => id !== groupId
                    ),
                  })
                }
              />
            </Tag>
          ))}
        </div>
        <div className="flex flex-col gap-6 mt-6 mb-6 lg:grid lg:grid-cols-9 min-h-lg">
          <Card className="col-span-5">
            <CardBody className="min-h-lg">
              {
                chartData && (
                  <UserGradeHorizontalBarChart
                    data={chartData}
                    options={options}
                    onBarClick={(datum) =>
                      setQuery({
                        userId: datum?.userId,
                      })
                    }
                  />
                )
              }
              {usersData && (
                <div className="pt-3">
                  <Pagination
                    page={usersPage}
                    itemsPerPage={20}
                    itemsCount={usersData.users.metadata.totalCount}
                    onChange={(page) => setQuery({ usersPage: page })}
                  />
                </div>
              )}
            </CardBody>
          </Card>
          <Card className="flex flex-col col-span-4">
            <CardHeader>
              <p className="flex flex-wrap items-baseline gap-x-2">
                {t("statistics.collaboratorEvaluatedAsIn", {
                  as: (
                    <Select
                      id={"evaluation-options-selector"}
                      items={options}
                      defaultValue={options.find(
                        ({ value }) => value === selectedLevel
                      )}
                      renderItem={(item) => item.label}
                      getValue={(item) => item.value.toString()}
                      onChange={(item) =>
                        setQuery({
                          selectedLevel: item.value,
                        })
                      }
                      label={""}
                      srOnlyLabel
                    />
                  ),
                })}
              </p>
            </CardHeader>
            <CardBody className="flex flex-col flex-grow overflow-hidden min-h-lg">
              <div className="flex-1">
                <List
                  isLoading={usersByLevelLoading}
                  emptyMessage={t("wombat.select.emptyResult") as string}
                  data={usersByLevel}
                  listOnly
                  renderAsList={(user: UserMinimalFragment) => {
                    return (
                      <li className="py-4" key={user.id}>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center flex-1 min-w-0 space-x-2 ">
                            <UserAvatar
                              src={user.avatar}
                              size={8}
                              alt={`${user.name} profile picture`}
                            />
                            <Link to={`/users/${user.id}`} className="text-sm">
                              {user.name}
                            </Link>
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  id="user-mapping-list"
                />
              </div>
              <div className="flex-shrink-0">
                {usersByLevelData && (
                  <div className="pt-3">
                    <Pagination
                      page={usersByLevelPage}
                      itemsPerPage={6}
                      itemsCount={usersByLevelData.users.metadata.totalCount}
                      onChange={(page) => setQuery({ usersByLevelPage: page })}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
        <EvaluationEvolutionDetail digest={digest} />
      </Container>
    </ScreenContainer>
  );
};

export default React.memo(EvaluationAnalyticsDetail);
